import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from '@phosphor-icons/react';
import clsx from 'clsx';

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  // When provided, an arrow-up-right icon renders inline beside the title
  // and links to this destination. This frees the `right` slot for other
  // controls (scope toggles, segmented buttons) on cards that also need a
  // jump-to-detail affordance.
  href?: string;
  // Accessible label for the inline arrow link. Defaults to "Open <title>"
  // when title is a plain string; explicitly required when title is JSX.
  hrefLabel?: string;
  size?: 'sm' | 'lg';
  flush?: boolean;
};

export function CardHeader({
  title,
  subtitle,
  right,
  href,
  hrefLabel,
  size = 'lg',
  flush = false,
}: Props) {
  const titleNode = href ? (
    <span className="inline-flex items-center gap-2">
      <span>{title}</span>
      <Link
        to={href}
        aria-label={
          hrefLabel ?? (typeof title === 'string' ? `Open ${title}` : 'Open detail')
        }
        className="text-fg-tertiary hover:text-fg transition-colors flex-none"
      >
        <ArrowUpRightIcon size={size === 'lg' ? 22 : 16} weight="bold" />
      </Link>
    </span>
  ) : (
    title
  );

  return (
    <div
      className={clsx(
        'flex items-baseline justify-between gap-3 flex-wrap',
        flush ? 'px-5 py-4 border-b border-border' : 'mb-4'
      )}
    >
      <div className="min-w-0">
        <h3 className={clsx('font-semibold', size === 'lg' ? 'text-xl sm:text-2xl' : 'text-base')}>
          {titleNode}
        </h3>
        {subtitle && (
          <p className="text-sm text-fg-tertiary mt-0.5">{subtitle}</p>
        )}
      </div>
      {right}
    </div>
  );
}
